"use client"

import type React from "react"
import { useEffect, useState, useMemo, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useSearchParams, useNavigate, useParams } from "react-router-dom"
import type { RootState } from "../store/store"
import { setProducts, setFilters, clearFilters } from "../store/slices/productsSlice"
import { productsService, categoriesService } from "../services/firebase"
import ProductCard from "../components/common/ProductCard"
import LoadingSpinner from "../components/common/LoadingSpinner"
import { X, Star, IndianRupee, Grid, List, Filter, Search, ChevronRight, Home } from "lucide-react"
import SEOHead from "../components/common/SEOHead"
import type { Category } from "../types"
import { Link } from "react-router-dom"

const Products: React.FC = () => {
  const { category: categorySlug } = useParams<{ category?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid")
  const [isLoading, setIsLoading] = useState(true)
  const [categories, setCategories] = useState<Category[]>([])

  const { filteredItems: products } = useSelector((state: RootState) => state.products)

  const [localFilters, setLocalFilters] = useState({
    priceRange: [0, 100000] as [number, number],
    rating: 0,
    sortBy: "name",
    inStock: false,
    search: "",
  })

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    // Apply URL search params
    const sort = searchParams.get("sort")
    const minPrice = searchParams.get("minPrice")
    const maxPrice = searchParams.get("maxPrice")
    const rating = searchParams.get("rating")
    const search = searchParams.get("search")

    const newFilters: Record<string, unknown> = {}
    if (sort) newFilters.sortBy = sort
    if (search) newFilters.search = search
    if (minPrice || maxPrice) {
      newFilters.priceRange = [minPrice ? Number.parseInt(minPrice) : 0, maxPrice ? Number.parseInt(maxPrice) : 100000]
    }
    if (rating) newFilters.rating = Number.parseInt(rating)

    if (Object.keys(newFilters).length > 0) {
      dispatch(setFilters(newFilters))
      setLocalFilters((prev) => ({ ...prev, ...newFilters }))
    }
  }, [searchParams, dispatch])

  const loadData = async () => {
    try {
      setIsLoading(true)
      const [productsData, categoriesData] = await Promise.all([
        productsService.getProducts(),
        categoriesService.getCategories(),
      ])
      dispatch(setProducts(productsData))
      setCategories(categoriesData)
    } catch (error) {
      console.error("Error loading data:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFilterChange = (filterType: string, value: unknown) => {
    const newFilters = { ...localFilters, [filterType]: value }
    setLocalFilters(newFilters)

    // Update URL params
    const params = new URLSearchParams(searchParams)
    if (filterType === "sortBy") {
      params.set("sort", value as string)
    } else if (filterType === "priceRange") {
      const [min, max] = value as [number, number]
      params.set("minPrice", min.toString())
      params.set("maxPrice", max.toString())
    } else if (filterType === "rating") {
      if (value) {
        params.set("rating", (value as number).toString())
      } else {
        params.delete("rating")
      }
    } else if (filterType === "search") {
      if (value) {
        params.set("search", value as string)
      } else {
        params.delete("search")
      }
    }
    setSearchParams(params)

    // Apply to Redux
    dispatch(setFilters({ [filterType]: value }))
  }

  const clearAllFilters = () => {
    setLocalFilters({
      priceRange: [0, 100000],
      rating: 0,
      sortBy: "name",
      inStock: false,
      search: "",
    })
    setSearchParams({})
    dispatch(clearFilters())
  }

  const getActiveFiltersCount = () => {
    let count = 0
    if (localFilters.rating > 0) count++
    if (localFilters.priceRange[0] > 0 || localFilters.priceRange[1] < 100000) count++
    if (localFilters.inStock) count++
    if (localFilters.search) count++
    return count
  }

  // Memoize filtered products for rendering
  const memoizedProducts = useMemo(() => {
    let filtered = [...products]
    if (categorySlug) {
      filtered = filtered.filter(
        (product) => Array.isArray(product.categories) && product.categories.includes(categorySlug)
      )
    }
    return filtered
  }, [products, categorySlug])

  // Find the current category object
  const currentCategory = useMemo(() => {
    if (!categorySlug) return null
    return categories.find((cat) => cat.slug === categorySlug) || null
  }, [categorySlug, categories])

  // Memoize event handlers
  const memoizedHandleFilterChange = useCallback(handleFilterChange, [localFilters, searchParams, dispatch])
  const memoizedClearAllFilters = useCallback(clearAllFilters, [dispatch, setSearchParams])
  const memoizedGetActiveFiltersCount = useCallback(getActiveFiltersCount, [localFilters])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <>
      <SEOHead
        title={
          currentCategory
            ? `${currentCategory.name} Products | Anmol Furniture Bazaar`
            : "All Products | Anmol Furniture Bazaar"
        }
        description={
          currentCategory
            ? `Browse premium ${currentCategory.name.toLowerCase()} at Anmol Furniture Bazaar. Shop now for quality, warranty, and free delivery.`
            : "Browse all premium furniture at Anmol Furniture Bazaar. Shop beds, sofas, dining sets, wardrobes, and more."
        }
        keywords={
          currentCategory
            ? `${currentCategory.name.toLowerCase()}, premium ${currentCategory.name.toLowerCase()}, furniture, buy ${currentCategory.name.toLowerCase()} online`
            : "furniture, home furniture, beds, sofas, dining table, wardrobe, chairs, tables, premium furniture"
        }
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: currentCategory ? `${currentCategory.name} Products` : "All Products",
          itemListElement: memoizedProducts.map((product, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            url: `https://anmolfurniture.com/products/${product.id}`,
            name: product.name,
            image: product.images?.[0],
            description: product.description,
          })),
        }}
      />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Breadcrumbs */}
          <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
            <Link to="/" className="hover:text-primary-600">
              <Home className="w-4 h-4" />
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/categories" className="hover:text-primary-600">
              Categories
            </Link>
            {currentCategory && (
              <>
                <ChevronRight className="w-4 h-4" />
                <span className="text-gray-900 font-medium">{currentCategory.name}</span>
              </>
            )}
          </nav>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {currentCategory ? `${currentCategory.name} Products` : "All Products"}
            </h1>
            <p className="text-gray-600">
              {memoizedProducts.length} {currentCategory ? currentCategory.name : "products"} found
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search products..."
                value={localFilters.search}
                onChange={(e) => memoizedHandleFilterChange("search", e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center space-x-4">
              {/* Mobile Filter Button */}
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="md:hidden flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Filter className="w-5 h-5" />
                <span>Filters</span>
                {memoizedGetActiveFiltersCount() > 0 && (
                  <span className="bg-primary-600 text-white text-xs px-2 py-1 rounded-full">
                    {memoizedGetActiveFiltersCount()}
                  </span>
                )}
              </button>

              {/* Clear Filters */}
              {memoizedGetActiveFiltersCount() > 0 && (
                <button
                  onClick={memoizedClearAllFilters}
                  className="flex items-center space-x-2 text-primary-600 hover:text-primary-700"
                >
                  <X className="w-4 h-4" />
                  <span>Clear Filters</span>
                </button>
              )}
            </div>

            <div className="flex items-center space-x-4">
              {/* Sort Dropdown */}
              <select
                value={localFilters.sortBy}
                onChange={(e) => memoizedHandleFilterChange("sortBy", e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="name">Sort by Name</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest First</option>
              </select>

              {/* View Mode Toggle */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 ${viewMode === "grid" ? "bg-primary-600 text-white" : "bg-white text-gray-600"}`}
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 ${viewMode === "list" ? "bg-primary-600 text-white" : "bg-white text-gray-600"}`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-8">
            {/* Filters Sidebar */}
            <div
              className={`w-full md:w-64 bg-white rounded-lg shadow-md p-6 ${isFilterOpen ? "block" : "hidden md:block"}`}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                <button onClick={() => setIsFilterOpen(false)} className="md:hidden">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Price Range</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <IndianRupee className="w-4 h-4 text-gray-500" />
                    <input
                      type="number"
                      value={localFilters.priceRange[0]}
                      onChange={(e) =>
                        handleFilterChange("priceRange", [Number(e.target.value), localFilters.priceRange[1]])
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Min"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <IndianRupee className="w-4 h-4 text-gray-500" />
                    <input
                      type="number"
                      value={localFilters.priceRange[1]}
                      onChange={(e) =>
                        handleFilterChange("priceRange", [localFilters.priceRange[0], Number(e.target.value)])
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Max"
                    />
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Rating</h3>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="rating"
                      checked={localFilters.rating === 0}
                      onChange={() => handleFilterChange("rating", 0)}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">All Ratings</span>
                  </label>
                  {[4, 3, 2, 1].map((rating) => (
                    <label key={rating} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="rating"
                        checked={localFilters.rating === rating}
                        onChange={() => handleFilterChange("rating", rating)}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex items-center">
                        {Array.from({ length: rating }).map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        ))}
                        <span className="text-sm text-gray-700 ml-2">& Up</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Stock */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Availability</h3>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={localFilters.inStock}
                    onChange={(e) => handleFilterChange("inStock", e.target.checked)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">In Stock Only</span>
                </label>
              </div>
            </div>

            {/* Products Grid/List */}
            <div className="flex-1">
              {memoizedProducts.length === 0 ? (
                <div className="text-center py-16">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
                  <p className="text-gray-600">Try adjusting your filters or search terms</p>
                </div>
              ) : (
                <div
                  className={viewMode === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-6"}
                >
                  {memoizedProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      className={viewMode === "list" ? "flex flex-row space-x-4" : ""}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Products
